import { getChatSessionsOptions } from '@onefootprint/fetch-dashboard';
import type { ChatSession } from '@onefootprint/request-types/dashboard';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { peekChatSessionIsLive, rememberChatSessionEnv } from '@/components/agent-chat/utils/chat-session-stash';
import useSession from '@/hooks/use-session';

const PAGE_SIZE = 50;

const parseIsLiveParam = (value: string | string[] | undefined): boolean | undefined => {
  const v = Array.isArray(value) ? value[0] : value;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return undefined;
};

const useLiveChatSessionsQuery = () => {
  const { data: session } = useSession();
  const isSandboxRestricted = session.org?.isSandboxRestricted;
  return useQuery({
    ...getChatSessionsOptions({ query: { pageSize: PAGE_SIZE }, headers: { 'x-is-live': 'true' } }),
    enabled: !isSandboxRestricted,
  });
};

const useSandboxChatSessionsQuery = () =>
  useQuery(getChatSessionsOptions({ query: { pageSize: PAGE_SIZE }, headers: { 'x-is-live': 'false' } }));

const useChatSessionsList = () => {
  const liveQuery = useLiveChatSessionsQuery();
  const sandboxQuery = useSandboxChatSessionsQuery();

  const sessions: ChatSession[] = [...(liveQuery.data?.data ?? []), ...(sandboxQuery.data?.data ?? [])].sort((a, b) =>
    a.createdAt < b.createdAt ? 1 : -1,
  );

  return {
    sessions,
    isLoading: sandboxQuery.isLoading || liveQuery.isLoading,
  };
};

/**
 * Resolves a chat's env from the cached lists so per-chat fetches can send the
 * matching `x-is-live` header — the detail handlers scope by env and 404 otherwise.
 * Returns `undefined` until both lists settle; then defaults to `true` so a
 * missing id 404s naturally.
 *
 * A shared-link recipient won't find the session in their own lists (shared
 * sessions are owner-scoped in `ChatSession::list`), so the copied link carries
 * the env as `?is_live=`; that takes precedence here to avoid 404ing a sandbox chat.
 */
export const useChatSessionIsLive = (chatSessionId: string | undefined): boolean | undefined => {
  const router = useRouter();
  const liveQuery = useLiveChatSessionsQuery();
  const sandboxQuery = useSandboxChatSessionsQuery();
  if (!chatSessionId) return undefined;
  // Env hint from a shared link, e.g. /chat/<id>?is_live=false.
  const linkIsLive = parseIsLiveParam(router.query.is_live);
  if (linkIsLive !== undefined) return linkIsLive;
  // A just-created chat isn't in either cached list yet, so seed its env from
  // the stash to avoid the `true` fallback below (which would 404 a sandbox chat).
  const stashedIsLive = peekChatSessionIsLive(chatSessionId);
  if (stashedIsLive !== undefined) return stashedIsLive;
  // Resolve from the lists, then pin — so a later list refetch can't reset env to
  // `undefined` and re-key the per-chat queries (reload + scroll reset).
  if (liveQuery.data?.data.some(s => s.id === chatSessionId)) {
    rememberChatSessionEnv(chatSessionId, true);
    return true;
  }
  if (sandboxQuery.data?.data.some(s => s.id === chatSessionId)) {
    rememberChatSessionEnv(chatSessionId, false);
    return false;
  }
  if (!liveQuery.isLoading && !sandboxQuery.isLoading) return true;
  return undefined;
};

export default useChatSessionsList;
